import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Button, Autocomplete, Box,
  FormControlLabel, Checkbox, Stack, IconButton, Typography, Divider
} from '@mui/material';
import { Delete, Close } from '@mui/icons-material';
import { collection, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

function isoToLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function isoToLocalDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function localToIso(local, allDay) {
  if (!local) return null;
  if (allDay) return new Date(local + 'T00:00:00').toISOString();
  return new Date(local).toISOString();
}

function formatContactLabel(c) {
  const nome = (c?.nome || '').trim();
  const azienda = (c?.azienda || '').trim();
  if (azienda && nome && azienda !== nome) return `${azienda} — ${nome}`;
  return azienda || nome || 'Contatto senza nome';
}

const FREQ_OPTIONS = [
  { value: 'none',    label: 'Non si ripete' },
  { value: 'daily',   label: 'Ogni giorno' },
  { value: 'weekly',  label: 'Ogni settimana' },
  { value: 'monthly', label: 'Ogni mese' }
];

const END_OPTIONS = [
  { value: 'never', label: 'Mai' },
  { value: 'count', label: 'Dopo N volte' },
  { value: 'until', label: 'Fino al…' }
];

export default function EventModal({ initial, contatti, staff, isAdmin, onClose, onDelete }) {
  const [saving, setSaving] = useState(false);
  const isEditing = !!initial?.id;

  const initRic = initial?.ricorrenza || null;

  const [f, setF] = useState({
    titolo:     initial?.titolo     || '',
    allDay:     !!initial?.allDay,
    start:      initial?.start      || new Date().toISOString(),
    end:        initial?.end        || new Date().toISOString(),
    contattoId: initial?.contattoId || null,
    luogo:      initial?.luogo      || '',
    note:       initial?.note       || '',
    visibility: initial?.visibility || 'public',
    owner:      initial?.owner      || staff?.nome || 'Mario',
    // ricorrenza
    freq:       initRic?.freq  || 'none',
    interval:   initRic?.interval || 1,
    endType:    initRic ? (initRic.count ? 'count' : initRic.until ? 'until' : 'never') : 'never',
    count:      initRic?.count || 10,
    until:      initRic?.until ? isoToLocalDate(initRic.until) : ''
  });

  const setF_ = (patch) => setF(prev => ({ ...prev, ...patch }));

  const buildRicorrenza = () => {
    if (f.freq === 'none') return null;
    return {
      freq:     f.freq,
      interval: Number(f.interval) || 1,
      count:    f.endType === 'count' ? (Number(f.count) || 10) : null,
      until:    f.endType === 'until' && f.until ? new Date(f.until + 'T23:59:59').toISOString() : null
    };
  };

  const handleSave = async () => {
    if (!f.titolo.trim()) { alert('Inserisci un titolo.'); return; }
    setSaving(true);
    try {
      const payload = {
        titolo:      f.titolo.trim(),
        allDay:      f.allDay,
        start:       f.start,
        end:         f.end,
        contattoId:  f.contattoId || null,
        luogo:       f.luogo.trim(),
        note:        f.note.trim(),
        visibility:  f.visibility,
        owner:       f.owner,
        ricorrenza:  buildRicorrenza(),
        updatedAt:   serverTimestamp()
      };
      if (isEditing) {
        await updateDoc(doc(db, 'crm_eventi', initial.id), payload);
      } else {
        await addDoc(collection(db, 'crm_eventi'), {
          ...payload,
          createdAt:  serverTimestamp(),
          createdBy:  staff?.nome || 'Sistema'
        });
      }
      onClose();
    } catch (e) {
  console.error('ERRORE SALVATAGGIO:', e.code, e.message);
  alert('Errore nel salvataggio. Riprova.');
}
  };

  const selectedContact = contatti.find(c => c.id === f.contattoId) || null;
  const canEdit = !isEditing || isAdmin || initial?.owner === staff?.nome;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {isEditing ? 'Modifica evento' : 'Nuovo evento'}
        <IconButton size="small" onClick={onClose}><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {!canEdit && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.lighter', border: 1, borderColor: 'warning.light', borderRadius: 1 }}>
            <Typography variant="body2" color="warning.dark">
              Sola lettura — questo evento appartiene a {initial?.owner}.
            </Typography>
          </Box>
        )}

        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Titolo" value={f.titolo} required autoFocus fullWidth disabled={!canEdit}
            onChange={e => setF_({ titolo: e.target.value })}
          />

          <FormControlLabel
            control={<Checkbox checked={f.allDay} disabled={!canEdit} onChange={e => setF_({ allDay: e.target.checked })} />}
            label="Tutto il giorno"
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Inizio" fullWidth InputLabelProps={{ shrink: true }} disabled={!canEdit}
              type={f.allDay ? 'date' : 'datetime-local'}
              value={f.allDay ? isoToLocalDate(f.start) : isoToLocal(f.start)}
              onChange={e => setF_({ start: localToIso(e.target.value, f.allDay) })}
            />
            <TextField
              label="Fine" fullWidth InputLabelProps={{ shrink: true }} disabled={!canEdit}
              type={f.allDay ? 'date' : 'datetime-local'}
              value={f.allDay ? isoToLocalDate(f.end) : isoToLocal(f.end)}
              onChange={e => setF_({ end: localToIso(e.target.value, f.allDay) })}
            />
          </Stack>

          <Autocomplete
            options={contatti}
            value={selectedContact}
            onChange={(e, v) => setF_({ contattoId: v?.id || null })}
            getOptionLabel={formatContactLabel}
            renderInput={p => <TextField {...p} label="Contatto collegato (opzionale)" />}
            disabled={!canEdit}
          />

          <TextField
            label="Luogo / Link" placeholder="Es. Sassari, Google Meet, telefonata"
            value={f.luogo} fullWidth disabled={!canEdit}
            onChange={e => setF_({ luogo: e.target.value })}
          />

          <TextField
            label="Note" value={f.note} fullWidth multiline rows={2} disabled={!canEdit}
            onChange={e => setF_({ note: e.target.value })}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select label="Visibilità" value={f.visibility} fullWidth disabled={!canEdit}
              onChange={e => setF_({ visibility: e.target.value })}
              helperText={f.visibility === 'public' ? 'Visibile a tutto il team' : 'Solo tu lo vedi'}
            >
              <MenuItem value="public">Pubblico</MenuItem>
              <MenuItem value="private">Privato</MenuItem>
            </TextField>

            {isAdmin ? (
              <TextField
                select label="Proprietario" value={f.owner} fullWidth disabled={!canEdit}
                onChange={e => setF_({ owner: e.target.value })}
              >
                <MenuItem value="Mario">Mario</MenuItem>
                <MenuItem value="Giuseppina">Giuseppina</MenuItem>
              </TextField>
            ) : (
              <TextField label="Proprietario" value={f.owner} fullWidth disabled />
            )}
          </Stack>

          {/* ── RICORRENZA ── */}
          <Divider />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Ricorrenza</Typography>

          <TextField
            select label="Si ripete" value={f.freq} fullWidth disabled={!canEdit}
            onChange={e => setF_({ freq: e.target.value })}
          >
            {FREQ_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>

          {f.freq !== 'none' && (
            <Stack spacing={2}>
              <TextField
                type="number" label="Ogni quante unità" value={f.interval}
                inputProps={{ min: 1, max: 99 }} fullWidth disabled={!canEdit}
                onChange={e => setF_({ interval: e.target.value })}
                helperText={
                  f.freq === 'daily'   ? `Ogni ${f.interval} giorn${f.interval == 1 ? 'o' : 'i'}` :
                  f.freq === 'weekly'  ? `Ogni ${f.interval} settiman${f.interval == 1 ? 'a' : 'e'}` :
                  f.freq === 'monthly' ? `Ogni ${f.interval} mes${f.interval == 1 ? 'e' : 'i'}` : ''
                }
              />

              <TextField
                select label="Fine ricorrenza" value={f.endType} fullWidth disabled={!canEdit}
                onChange={e => setF_({ endType: e.target.value })}
              >
                {END_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>

              {f.endType === 'count' && (
                <TextField
                  type="number" label="Numero di ripetizioni" value={f.count}
                  inputProps={{ min: 1, max: 365 }} fullWidth disabled={!canEdit}
                  onChange={e => setF_({ count: e.target.value })}
                  helperText={`L'evento si ripeterà ${f.count} volt${f.count == 1 ? 'a' : 'e'} in totale`}
                />
              )}

              {f.endType === 'until' && (
                <TextField
                  type="date" label="Fino al" value={f.until}
                  InputLabelProps={{ shrink: true }} fullWidth disabled={!canEdit}
                  onChange={e => setF_({ until: e.target.value })}
                />
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Box>
          {isEditing && canEdit && (
            <Button color="error" startIcon={<Delete />} onClick={() => onDelete(initial.id)}>
              Elimina
            </Button>
          )}
        </Box>
        <Box>
          <Button onClick={onClose} sx={{ mr: 1 }}>Annulla</Button>
          {canEdit && (
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Salvataggio…' : 'Salva'}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}